import React, { useContext, useEffect, useState, useCallback } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getPopularAirports } from 'actions/FlightActions'
import { PopularAirports } from 'components/Flights/types'
import useAirportSearch from 'hooks/Flights/useAirportSearch'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import Caption from 'components/Luxkit/Typography/Caption'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OfferAirportSelectOption from './OfferAirportSelectOption'
import noop from 'lib/noop'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferPageDispatchContext from 'contexts/OfferPage/offerPageDispatchContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'

interface Props {
  selectedAirport?: App.Airport;
  popularAirports: PopularAirports;
  onSelectAirport?: (airport: App.AirportLocation) => void;
}

function OfferAirportOptions(props: Props) {
  const {
    selectedAirport,
    popularAirports,
    onSelectAirport = noop,
  } = props
  const [searchPhrase, setSearchPhrase] = useState('')

  const dispatch = useAppDispatch()
  const offerPageDispatch = useContext(OfferPageDispatchContext)

  useEffect(() => {
    dispatch(getPopularAirports())
  }, [dispatch])

  const [airports, loading] = useAirportSearch(searchPhrase)

  const onChangeInputText = useCallback(e => {
    setSearchPhrase(e.target.value)
  }, [])

  const onItemClick = useCallback((airport: App.AirportLocation) => {
    offerPageDispatch({
      type: OfferPageActions.setAirport,
      airport: {
        code: airport.airportCode,
        name: airport.airportName,
        latitude: airport.lt!,
        longitude: airport.lg!,
      },
    })
    onSelectAirport(airport)
  }, [offerPageDispatch, onSelectAirport])

  return <VerticalSpacer gap={20}>
    <TextInputOnly
      placeholder="Search by city or airport"
      onChange={onChangeInputText}
      endIcon={<LineSearchIcon />}
    />
    <LoadingIndicator floating visible={loading} />
    {searchPhrase.length < 2 && <VerticalSpacer gap={8}>
      <Caption variant="medium" colour="neutral-three">
        Airports near you
      </Caption>
      <div>
        {popularAirports.origins.map(airport => <OfferAirportSelectOption
          key={airport.airportCode}
          airport={airport}
          onClick={onItemClick}
          selected={airport.airportCode === selectedAirport?.code}
          data-testid={`airport-select-${airport.airportCode}`}
        />)}
      </div>
    </VerticalSpacer>}

    {searchPhrase.length > 1 && <div>
      {airports.map(airport => <OfferAirportSelectOption
        key={airport.airportCode}
        airport={airport}
        onClick={onItemClick}
        selected={airport.airportCode === selectedAirport?.code}
        data-testid={`airport-select-${airport.airportCode}`}
      />)}
    </div>}
  </VerticalSpacer>
}

export default withOfferPageAndReduxState((state, appState) => ({
  popularAirports: appState.flights.popularAirports,
  selectedAirport: state.airport,
}))(OfferAirportOptions)
